import Moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { getCalendarDaysWithFreeIntervals } from 'src/services/api/calendar';
import makeCancelable, { ICancelablePromise } from 'src/services/cancelablePromise';
import { getDate, isValidDate, getYearMonthFormat } from 'src/services/dateTime';
import { IDateTimeSelectBase } from '../DateTimeSelect';
import {
    getServiceDaysWithFreeIntervals,
    getServiceMaxWeeksForward,
} from 'src/services/api/service';
import { registerDateFnsLocale } from '../../helpers';
import { getCalendarMaxDate } from 'src/entities/calendar';
import { useIntl } from 'react-intl';

interface IDatePickerProps extends IDateTimeSelectBase {
    firstFreeDay: string;
    value: string;
    onChange: (selectedDate: string) => void;
    locale: string;
    calendarChanged?: boolean;
    clickedByUser?: boolean;
}

const DatePicker: React.FC<IDatePickerProps> = ({
    firstFreeDay,
    value,
    onChange,
    calendarChanged,
    clickedByUser,
    autoSelect,
    calendar,
    isOnlyService,
    service,
    personCount,
    pendingReservations,
    modifiedReservationId,
    allowAlreadySelected,
}) => {
    const intl = useIntl();
    const freeDaysSubscription = useRef<ICancelablePromise<string[] | null>>();
    const maxWeeksForwardSubscription = useRef<ICancelablePromise<number>>();
    registerDateFnsLocale(intl.locale);
    const [maxWeeksForward, setMaxWeeksForward] = useState(1);
    const [hasLoadedMaxWeeksForward, setHasLoadedMaxWeeksForward] = useState(false);
    const [hasAutoSelected, setHasAutoSelected] = useState(false);
    const [moreFetchNeeded, setMoreFetchNeeded] = useState(!!autoSelect);
    const [freeDays, setFreeDays] = useState<string[]>([]);
    const maxDate = getCalendarMaxDate(calendar?.WeeksForward || maxWeeksForward);
    const handleMonthChange = (month: Date) => {
        fetchFreeDays(month);
    };

    const getExcludedDays = (
        min: string | Date,
        max: string | Date,
        includedDays: Array<string | Date>
    ) => {
        const days = [];
        for (const i = Moment(min); i.isSameOrBefore(max, 'day'); i.add(1, 'day')) {
            days.push(i.clone());
        }

        return days
            .filter((day) => !includedDays.find((id) => day.isSame(id)))
            .map((day) => day.toDate());
    };

    const excludedDays = getExcludedDays(firstFreeDay, maxDate, freeDays);

    const handleDateChange = (date: Date) => {
        const stringDate = Moment(date).hour(12).toISOString();
        onChange(stringDate);
    };

    const getMaxWeeksForward = async () => {
        if (!calendar && isOnlyService && service && !hasLoadedMaxWeeksForward) {
            maxWeeksForwardSubscription.current = makeCancelable<number>(
                getServiceMaxWeeksForward(service.Id)
            );

            try {
                const result = await maxWeeksForwardSubscription.current.promise;
                if (result) {
                    setMaxWeeksForward(result);
                    setHasLoadedMaxWeeksForward(true);
                    return result;
                }
            } catch (e) {
                return maxWeeksForward;
            }
        }

        return maxWeeksForward;
    };

    const fetchFreeDays = async (month: Date) => {
        let startDateMoment = Moment(month).startOf('month');

        if ((isOnlyService || calendar) && service && startDateMoment) {
            const currentMaxWeeksForward = await getMaxWeeksForward();
            const endOfWeeksForward = Moment()
                .add(calendar?.WeeksForward || currentMaxWeeksForward, 'weeks')
                .endOf('week');

            if (startDateMoment.isAfter(endOfWeeksForward)) {
                startDateMoment = Moment(firstFreeDay).startOf('month');
            }

            const endOfMonth = Moment(startDateMoment).endOf('month');
            const startDate = startDateMoment.toISOString();
            const endDate = Moment.min([endOfMonth, endOfWeeksForward]).toISOString();

            freeDaysSubscription.current = makeCancelable<string[] | null>(
                !isOnlyService && calendar
                    ? getCalendarDaysWithFreeIntervals({
                          calendarId: calendar.Id,
                          serviceId: service.Id,
                          personCount,
                          startDate,
                          endDate,
                          pendingReservations,
                          modifiedReservationId,
                      })
                    : getServiceDaysWithFreeIntervals({
                          serviceId: service.Id,
                          personCount,
                          startDate,
                          endDate,
                          pendingReservations,
                          modifiedReservationId,
                      })
            );

            freeDaysSubscription.current.promise
                .then((result) => {
                    if (result) {
                        if (allowAlreadySelected && autoSelect) {
                            result.push(autoSelect);
                        }
                        result.sort();
                        setFreeDays(result);
                    }
                })
                .catch(() => null);
        }
    };

    const autoSelectDate = () => {
        if (!freeDays.length || hasAutoSelected) {
            return;
        }

        if (autoSelect && isValidDate(autoSelect, freeDays)) {
            onChange(getDate(autoSelect));
        } else if (
            (value && calendarChanged && !clickedByUser) ||
            !value ||
            (value && !isValidDate(value, freeDays))
        ) {
            onChange(firstFreeDay);
        }

        setHasAutoSelected(true);
    };

    const checkAutoSelect = () => {
        if (moreFetchNeeded && freeDays.length) {
            if (autoSelect && !isValidDate(autoSelect, freeDays)) {
                fetchFreeDays(new Date(firstFreeDay));
            }
            setMoreFetchNeeded(false);
        }
    };

    useEffect(() => {
        if ((calendar || isOnlyService) && service) {
            let fetchDate = value;
            const date: string =
                autoSelect &&
                Moment(autoSelect).isBefore(
                    Moment()
                        .add(calendar?.WeeksForward || maxWeeksForward, 'weeks')
                        .endOf('week')
                )
                    ? autoSelect
                    : firstFreeDay;
            
            if (!modifiedReservationId && (!fetchDate || fetchDate < date)) {
                fetchDate = date;
            }

            fetchFreeDays(new Date(fetchDate));
        }

        autoSelectDate();

        return () => {
            freeDaysSubscription?.current?.cancel();
            maxWeeksForwardSubscription?.current?.cancel();
        };
    }, []);

    useEffect(() => {
        autoSelectDate();
        checkAutoSelect();
    }, [
        autoSelect,
        value,
        firstFreeDay,
        calendarChanged,
        clickedByUser,
        freeDays,
        onChange,
        hasAutoSelected,
        moreFetchNeeded,
    ]);

    return (
        <ReactDatePicker
            inline={true}
            locale={intl.locale}
            selected={value && isValidDate(value, freeDays) ? new Date(value) : undefined}
            minDate={new Date(firstFreeDay)}
            maxDate={new Date(maxDate)}
            excludeDates={excludedDays}
            onChange={handleDateChange}
            onMonthChange={handleMonthChange}
            dateFormatCalendar={getYearMonthFormat(intl.locale)}
        />
    );
};

export default DatePicker;
